import { ReactElement } from "react";
import { Link } from "react-router-dom";
import { useDependency } from "../../../contexts/DependencyContext";
import { useAsyncCall } from "../../../hooks/UseAsyncCall";
import { IComponent } from "../../../services/http/component/models/IComponent";
import Spinner from "../../../components/Shared/Spinner";
import Sidebar from "../../../components/Shared/Sidebar";
import { ComponentModelService } from "../../../services/ComponentModelService";
import { IComponentInfoResult } from "../../../services/http/component/models/IComponentInfoResult";
import SidebarWidget from "../../../components/Shared/Sidebar/SidebarWidget";
import { IBranch } from "../../../services/http/component/models/IBranch";
import styles from "./ComponentSidePanel.module.scss";
import { ComponentRowViewModel } from "../ComponentTableViewModel";

interface ISidePanelProps
{
    componentRow: ComponentRowViewModel | undefined;
    component: IComponent;
    getBranches: (id: number) => IBranch[] | undefined;
    setBreadcrumb: (componentRow: ComponentRowViewModel, full?: boolean) => ReactElement;
    close: () => void;
}

async function loadModelData(service: ComponentModelService, componentId: string): Promise<IComponentInfoResult>
{
    return await service.loadComponentModel(componentId.toString(), undefined!);
}

const ComponentSidePanel = (props: ISidePanelProps): ReactElement =>
{
    const componentModelService = useDependency(ComponentModelService);
    const modelContent = useAsyncCall(loadModelData, componentModelService, props.component.id);

    const SidebarHeader = (): ReactElement =>
    {
        return <span>{props.component.name}</span>;
    };

    const SidebarFooter = (): ReactElement =>
    {
        return (
            <Link to={`/dashboard/${props.component.id}`} className="btn btn-info-light">
                Review in dashboard
                <i className="material-icons">arrow_forward</i>
            </Link>
        );
    }

    const setBreadcrumb = (): ReactElement =>
    {
        if (props.componentRow && props.componentRow.branches && props.componentRow.branches.length > 0)
        {
            return props.setBreadcrumb(props.componentRow, true)
        }
        
        return <></>;
    }

    return (
        <Sidebar
            customClassName={[styles.sidePanel, props.component ? styles.visible : ""].join(" ")}
            header={<SidebarHeader />}
            footer={<SidebarFooter />}
            close={props.close}
            collapsible={true}
        >
            {modelContent.loading && <Spinner caption="Loading..." />}

            {!modelContent.loading && modelContent.content && (
                <>
                    <div className={styles.breadcrumb}>
                        {setBreadcrumb()}
                    </div>
                    
                    {modelContent.content.widgets.map((widget, index) =>
                    {
                        const configuration = JSON.parse(widget.uiConfiguration);

                        return (
                            <SidebarWidget
                                widget={widget}
                                index={index}
                                key={index}
                                visualClasses={configuration.visualClasses}
                            />
                        )
                    })}
                </>
            )}
        </Sidebar>
    );
};

export default ComponentSidePanel;
